import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Alert,
    Card,
    CardContent,
    Grid,
    Divider
} from '@mui/material';
import {
    CheckCircle,
    FileUpload
} from '@mui/icons-material';

import ImportCsvForm from '@/components/ImportCsvForm';
import { postApiVariablesBulkIdPlanta } from '@/api/gen/api'; 

interface StepVariablesRegisterClientProps {
    onNext: (variablesData: any) => void; 
    onBack: () => void;
    clientData: {
        id: number;
        clientName: string;
    };
    plantData: {
        id: number;
        plantName: string;
    };
}

// ==================== COMPONENTE PRINCIPAL ====================
const StepVariablesRegisterClient: React.FC<StepVariablesRegisterClientProps> = ({
    onNext,
    onBack,
    clientData,
    plantData
}) => {
    // ==================== ESTADOS ====================
    const [isLoading, setIsLoading] = useState(false);
    const [importCompleted, setImportCompleted] = useState(false);
    const [importResults, setImportResults] = useState<{
        createdZones?: string[];
        existingZones?: string[];
        createdTables?: string[];
        existingTables?: string[];
        createdVariables?: string[];
        existingVariables?: string[];
        errors?: string[];
        totalVariables?: number;
        apiResponse?: any;
    } | null>(null);

    // ==================== MANEJADORES ====================
    const handleCsvSubmit = async (data: { variables: any[] }) => {
        setIsLoading(true);
        try {
            console.log('📤 Creando variables desde CSV:', data.variables);
            console.log('🔑 ID Planta:', plantData.id);

            const bulkVariables = data.variables;

            console.log('🔄 Enviando a la API:', bulkVariables);

            // Llamar directamente a la función de la API
            const response = await postApiVariablesBulkIdPlanta(
                plantData.id,
                bulkVariables
            );

            console.log('✅ Respuesta de la API:', response);

            // Crear el objeto con los resultados
            const importResultsData = {
                createdVariables: data.variables.map(v => v.variableName),
                createdZones: [...new Set(data.variables.map(v => v.zoneName))],
                createdTables: [...new Set(data.variables.map(v => v.tableName))],
                totalVariables: data.variables.length,
                apiResponse: response
            };

            // Guardar en estado local
            setImportCompleted(true);
            setImportResults(importResultsData);

            // Retornar los resultados para que ImportCsvForm los use
            return {
                ...importResultsData,
                existingVariables: [],
                existingZones: [],
                existingTables: [],
                errors: []
            };

        } catch (error: any) {
            console.error('❌ Error creando variables:', error);
            
            let errorMessage = 'Error al crear las variables';
            let errors: string[] = [];

            if (error.response?.data?.errors) {
                errors = error.response.data.errors.map((err: any) => err.description);
                errorMessage = errors.join(', ');
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
                errors = [errorMessage];
            } else if (error.message) {
                errorMessage = error.message;
                errors = [errorMessage];
            }

            const errorResult = {
                createdVariables: [],
                createdZones: [],
                createdTables: [],
                existingVariables: [],
                existingZones: [],
                existingTables: [],
                errors: errors
            };

            setImportResults(errorResult);
            throw new Error(errorMessage);

        } finally {
            setIsLoading(false);
        }
    };

    const handleCompleteStep = () => {
        // Pasar los datos de variables al siguiente paso
        if (importResults) {
            onNext(importResults); // ← AHORA SÍ PASA LOS DATOS
        } else {
            onNext({ 
                createdVariables: [], 
                createdZones: [], 
                createdTables: [],
                totalVariables: 0,
                message: 'No se importaron variables' 
            });
        }
    };

    const handleSkipStep = () => {
        // Cuando se omite el paso, pasar datos vacíos
        onNext({
            createdVariables: [],
            createdZones: [],
            createdTables: [],
            totalVariables: 0,
            message: 'Paso omitido - No se importaron variables'
        });
    };

    const handleNewImport = () => {
        setImportCompleted(false);
        setImportResults(null);
    };

    // ==================== RENDERIZADO DE RESULTADOS ====================

    if (importCompleted) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
                    Configuración de Variables Completada
                </Alert>

                <Typography variant="h5" gutterBottom color="primary">
                    Paso 5 Completado: Variables Configuradas
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'success.main' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="success.main">
                            Resumen de la Configuración
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Información del Cliente y Planta */}
                            <Box>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Cliente y Planta
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body1">
                                            <strong>Cliente:</strong> {clientData.clientName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="body1">
                                            <strong>Planta:</strong> {plantData.plantName}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="body1">
                                            <strong>ID Planta:</strong> {plantData.id}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Resultados de la Importación */}
                            {importResults && (
                                <Box>
                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                        Resultados de la Configuración
                                    </Typography>
                                    
                                    {importResults.createdVariables && importResults.createdVariables.length > 0 && (
                                        <Alert severity="success" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">
                                                {importResults.createdVariables.length} variables creadas exitosamente
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
                                                {importResults.createdVariables.slice(0, 5).join(', ')}
                                                {importResults.createdVariables.length > 5 && ` y ${importResults.createdVariables.length - 5} más...`}
                                            </Typography>
                                        </Alert>
                                    )}

                                    {importResults.createdZones && importResults.createdZones.length > 0 && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">
                                                🏭 {importResults.createdZones.length} zonas configuradas
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
                                                {importResults.createdZones.join(', ')}
                                            </Typography>
                                        </Alert>
                                    )}

                                    {importResults.createdTables && importResults.createdTables.length > 0 && (
                                        <Alert severity="info" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">
                                                📊 {importResults.createdTables.length} tablas creadas
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
                                                {importResults.createdTables.join(', ')}
                                            </Typography>
                                        </Alert>
                                    )}

                                    {importResults.existingVariables && importResults.existingVariables.length > 0 && (
                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">
                                                ⚠️ {importResults.existingVariables.length} variables ya existían
                                            </Typography>
                                        </Alert>
                                    )}

                                    {importResults.errors && importResults.errors.length > 0 && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            <Typography variant="subtitle2">
                                                ❌ {importResults.errors.length} errores encontrados
                                            </Typography>
                                            <Typography variant="body2" sx={{ mt: 1, fontSize: '0.75rem' }}>
                                                {importResults.errors.join(', ')}
                                            </Typography>
                                        </Alert>
                                    )}
                                </Box>
                            )}

                            {/* Estado Final */}
                            <Box>
                                <Typography variant="body2" color="text.secondary">
                                    Estado del Sistema:
                                </Typography>
                                <Typography variant="body1" color="success.main" sx={{ mt: 1 }}>
                                    El cliente {clientData.clientName} con la planta {plantData.plantName} está completamente configurado y listo para usar.
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleNewImport}
                        disabled={isLoading}
                    >
                        Configurar Más Variables
                    </Button>
                    
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleCompleteStep}
                        startIcon={<CheckCircle />}
                    >
                        Completar Registro
                    </Button>
                </Box>
            </Box>
        );
    }

    // ==================== RENDERIZADO DEL FORMULARIO ====================

    return (
        <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mt: 1, mb: 3 }}>
                Importe las variables SCADA para la planta desde un archivo CSV. Este paso es opcional.
                <br />
                <strong>Las variables se asociarán automáticamente a la planta: {plantData.plantName} (ID: {plantData.id})</strong>
            </Alert>

            {/* Título */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <FileUpload color='primary' sx={{ fontSize: 32 }} />
                <Typography variant='h4' component='h1' fontWeight='bold'>
                    Importación de Variables SCADA
                </Typography>
            </Box>

            {/* Información de Resumen */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        backgroundColor: 'primary.50',
                        border: '1px solid',
                        borderColor: 'primary.200'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FileUpload color='primary' />
                                <Typography variant='h6' fontWeight='bold' color='primary.main'>
                                    Cliente
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body1">
                                    <strong>Nombre:</strong> {clientData.clientName}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>ID:</strong> {clientData.id}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        backgroundColor: 'info.50',
                        border: '1px solid',
                        borderColor: 'info.200'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <FileUpload color='info' />
                                <Typography variant='h6' fontWeight='bold' color='info.main'>
                                    Planta
                                </Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="body1">
                                    <strong>Nombre:</strong> {plantData.plantName}
                                </Typography>
                                <Typography variant="body1">
                                    <strong>ID:</strong> {plantData.id}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Formulario de Importación CSV */}
            <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Importar Variables desde CSV
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Las variables importadas se asociarán automáticamente a la planta: <strong>{plantData.plantName}</strong>
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <ImportCsvForm
                        onSubmit={handleCsvSubmit}
                        plantId={plantData.id}
                        isPending={isLoading}
                    />
                </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            {/* Botones de Navegación */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    Paso 5 de 5 - Configuración Final
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onBack}
                        disabled={isLoading}
                    >
                        Atrás
                    </Button>
                    
                    <Button
                        variant="outlined"
                        onClick={handleSkipStep} // ← CAMBIADO: usar handleSkipStep
                        disabled={isLoading}
                    >
                        Omitir este Paso
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default StepVariablesRegisterClient;